/**
 * Utility functions for language and locale detection/handling
 */
/**
 * Language variant enumeration for different regional variants of English
 */
export var LanguageVariant;
(function (LanguageVariant) {
    LanguageVariant["US_ENGLISH"] = "en-US";
    LanguageVariant["UK_ENGLISH"] = "en-GB";
    LanguageVariant["DEFAULT"] = "en-GB"; // Default to UK English
})(LanguageVariant || (LanguageVariant = {}));
/**
 * Detects user's language variant based on browser settings
 * @returns The detected language variant
 */
export function detectLanguageVariant() {
    // Check if browser API is available
    if (typeof navigator === 'undefined') {
        return LanguageVariant.DEFAULT;
    }
    // First, check if user has a saved preference
    const savedPreference = localStorage.getItem('userLanguagePreference');
    if (savedPreference && Object.values(LanguageVariant).includes(savedPreference)) {
        return savedPreference;
    }
    // Check navigator language (e.g., 'en-US', 'en-GB')
    const browserLanguage = navigator.language;
    // Direct match for supported variants
    if (browserLanguage === LanguageVariant.UK_ENGLISH) {
        return LanguageVariant.UK_ENGLISH;
    }
    if (browserLanguage === LanguageVariant.US_ENGLISH) {
        return LanguageVariant.US_ENGLISH;
    }
    // If just 'en', try to infer from timezone
    if (browserLanguage.startsWith('en')) {
        try {
            // Use timezone to guess regional variant
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            // Common UK timezones
            if (timezone === 'Europe/London' || timezone.includes('Europe/')) {
                return LanguageVariant.UK_ENGLISH;
            }
            // Common US timezones
            if (timezone.includes('America/') || timezone.startsWith('US/')) {
                return LanguageVariant.US_ENGLISH;
            }
        }
        catch (error) {
            console.warn('Error detecting timezone:', error);
        }
    }
    // Default to UK English if cannot determine
    return LanguageVariant.DEFAULT;
}
/**
 * Gets a date formatter for the specified language variant
 * @param variant The language variant
 * @returns Date formatting functions
 */
export function getDateFormatter(variant) {
    // Set options based on language variant
    const isUK = variant === LanguageVariant.UK_ENGLISH;
    return {
        formatShortDate: (date) => {
            return date.toLocaleDateString(variant, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        },
        formatLongDate: (date) => {
            return date.toLocaleDateString(variant, {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        },
        formatDateWithWeekday: (date) => {
            return date.toLocaleDateString(variant, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        },
        formatTime: (date) => {
            return date.toLocaleTimeString(variant, {
                hour: '2-digit',
                minute: '2-digit',
                hour12: !isUK // 12-hour clock for US, 24-hour for UK
            });
        }
    };
}
/**
 * Gets a number formatter for the specified language variant
 * @param variant The language variant
 * @returns Number formatting functions
 */
export function getNumberFormatter(variant) {
    const currencyCode = variant === LanguageVariant.UK_ENGLISH ? 'GBP' : 'USD';
    return {
        formatCurrency: (amount, code = currencyCode) => {
            return new Intl.NumberFormat(variant, {
                style: 'currency',
                currency: code
            }).format(amount);
        },
        formatDecimal: (num, decimalPlaces = 2) => {
            return new Intl.NumberFormat(variant, {
                minimumFractionDigits: decimalPlaces,
                maximumFractionDigits: decimalPlaces
            }).format(num);
        }
    };
}
